import React, { useState, useRef, useCallback } from 'react';
import { Trash2, Edit2, X } from 'lucide-react';

const SwipeableItem = ({ 
  children, 
  onDelete, 
  onEdit, 
  deleteLabel = "Delete",
  editLabel = "Edit",
  showEdit = true,
  showDelete = true 
}) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const currentOffset = useRef(0);
  const animationFrame = useRef(null);

  const maxSwipe = showEdit && showDelete ? -160 : -80;
  const snapThreshold = -60;

  const updateOffset = useCallback((offset) => {
    currentOffset.current = offset;
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
    animationFrame.current = requestAnimationFrame(() => {
      setSwipeOffset(offset);
    });
  }, []);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsSwiping(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (touchStartX.current === null) return;

    const deltaX = e.touches[0].clientX - touchStartX.current;
    const deltaY = e.touches[0].clientY - touchStartY.current;

    // If scrolling vertically more than horizontally, ignore
    if (Math.abs(deltaY) > Math.abs(deltaX) && !isOpen) {
      return;
    }

    // Prevent page scroll when swiping horizontally
    if (Math.abs(deltaX) > 10) {
      e.preventDefault();
    }

    let newOffset;
    if (isOpen) {
      // If already open, allow swiping back
      newOffset = Math.min(0, Math.max(maxSwipe, maxSwipe + deltaX));
    } else {
      // Only allow left swipe (negative)
      newOffset = Math.min(0, Math.max(maxSwipe, deltaX));
    }

    updateOffset(newOffset);
  }, [isOpen, maxSwipe, updateOffset]);

  const handleTouchEnd = useCallback(() => {
    setIsSwiping(false);
    touchStartX.current = null;
    touchStartY.current = null;

    // Snap to open or closed
    if (currentOffset.current < snapThreshold) {
      setSwipeOffset(maxSwipe);
      setIsOpen(true);
    } else {
      setSwipeOffset(0);
      setIsOpen(false);
    }

    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
  }, [maxSwipe, snapThreshold]);

  const handleClose = useCallback(() => {
    setSwipeOffset(0);
    setIsOpen(false);
  }, []);

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete();
      setSwipeOffset(0);
      setIsOpen(false);
    }
  }, [onDelete]);

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit();
      setSwipeOffset(0);
      setIsOpen(false);
    }
  }, [onEdit]);

  return (
    <div className="relative overflow-hidden rounded-lg">
      {/* Action Buttons (Behind) */}
      <div className="absolute right-0 top-0 bottom-0 flex items-stretch">
        {showEdit && onEdit && (
          <button
            onClick={handleEdit}
            className="flex flex-col items-center justify-center gap-1 bg-blue-600 text-white font-medium transition-colors hover:bg-blue-700 active:bg-blue-800"
            style={{ width: '80px' }}
          >
            <Edit2 size={20} />
            <span className="text-xs">{editLabel}</span>
          </button>
        )}
        {showDelete && onDelete && (
          <button
            onClick={handleDelete}
            className="flex flex-col items-center justify-center gap-1 bg-red-600 text-white font-medium transition-colors hover:bg-red-700 active:bg-red-800"
            style={{ width: '80px' }}
          >
            <Trash2 size={20} />
            <span className="text-xs">{deleteLabel}</span>
          </button>
        )}
      </div>

      {/* Swipeable Content */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translate3d(${swipeOffset}px, 0, 0)`,
          transition: isSwiping ? 'none' : 'transform 0.18s cubic-bezier(0.32, 0.72, 0, 1)',
          willChange: isSwiping ? 'transform' : 'auto',
        }}
        className="relative bg-white dark:bg-gray-800 touch-pan-y"
      >
        {children}
        
        {/* Close button when swiped open */}
        {isOpen && (
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 p-1.5 bg-gray-200 dark:bg-gray-700 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors shadow-sm"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SwipeableItem;